import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/** Información adicional a mostrar junto a la imagen en el visor. */
export interface LightboxInfo {
  pos: string | number
  titulo?: string
  descripcion?: string
}

interface Props {
  src: string
  alt?: string
  onClose: () => void
  info?: LightboxInfo
}

function CloseBtn({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Cerrar"
      className="absolute top-3 right-4 z-10 w-10 h-10 rounded-full bg-white/10 text-white text-xl hover:bg-white/20 transition-colors"
    >
      ✕
    </button>
  )
}

/** Visor de imagen a pantalla completa. Cierra con ✕, Esc o clic fuera.
 *  Si se pasa `info`, la foto ocupa el 70% (con margen alrededor, máx. 50vw × 80vh)
 *  y a la derecha flota un contenedor (30%) con la información adicional. */
export function ImagenLightbox({ src, alt = '', onClose, info }: Props) {
  const { t } = useTranslation()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!info) {
    return createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <CloseBtn onClose={onClose} />
        <img
          src={src}
          alt={alt}
          className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </div>,
      document.body,
    )
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col md:flex-row items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <CloseBtn onClose={onClose} />
      <div className="basis-[70%] shrink-0 grow-0 w-full min-h-0 flex items-center justify-center p-8">
        <img
          src={src}
          alt={alt}
          className="max-w-[85vw] max-h-[45vh] md:max-w-[50vw] md:max-h-[80vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="basis-[30%] shrink-0 grow-0 w-full flex items-center justify-center p-6">
        <div className="fc-lb-card" onClick={(e) => e.stopPropagation()}>
          <span className="fc-lb-pos">{t('bomCaptura.canvas.posicionN', { n: info.pos })}</span>
          {info.titulo && <h3 className="fc-lb-title">{info.titulo}</h3>}
          {info.descripcion && <p className="fc-lb-desc">{info.descripcion}</p>}
        </div>
      </div>
    </div>,
    document.body,
  )
}
